import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { RewardRuleEffects } from "../../types/enums";
import { IProductCatalogue } from "../../types/interfaces";
import { usePayLinkCart } from "../../pages/PayLink/context/PayLinkCartContext";
import { IProduct } from "../../pages/PayLink/types/campaigns";

// Used inline custom icon for AddIcon
const AddIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 4.16666V15.8333M4.16669 10H15.8334"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DotIcon = () => (
  <svg
    width="4"
    height="4"
    viewBox="0 0 4 4"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <circle cx="2" cy="2" r="2" fill="#71717A" />
  </svg>
);

const StarIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 1L7.545 4.13L11 4.635L8.5 7.07L9.09 10.51L6 8.885L2.91 10.51L3.5 7.07L1 4.635L4.455 4.13L6 1Z"
      fill="#FACC15"
    />
  </svg>
);

// Mock missing utility methods
const calculateDiscount = (
  price: number,
  value: string | number,
  type: string,
) => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return price - price * numValue;
};

const textWithOrWithOutHypen = ({
  text,
  withHypen,
}: {
  text?: string;
  withHypen?: boolean;
}) => {
  if (!text) return "";
  return withHypen ? text.replace(/\s+/g, "-").toLowerCase() : text;
};

// Mock routes
const HOME_URL = (country: string) => `/${country}`;
const STORES_URL = (country: string) => `/${country}/stores`;

// Mock RewardTypeIcon
const RewardTypeIcon = ({
  text,
  rewardType,
}: {
  text: string;
  rewardType?: string;
}) => {
  if (!text) return null;
  return (
    <div className="bg-py_green_1-300 text-py_black-950 text-[0.625rem] tracking-[-0.56px] font-semibold inline-flex items-center py-[2px] px-1 md:py-1 md:px-2 rounded-[100px] md:tracking-[-0.64px]">
      <span className="mr-1"></span> {text}
    </div>
  );
};

interface IProps {
  productItem: IProduct;
  onAddToCart?: (item: IProductCatalogue) => void;
  addingToCart?: boolean;
}

export const CartCard: React.FC<IProps> = ({
  productItem,
  onAddToCart,
  addingToCart = false,
}) => {
  const { cartItems } = usePayLinkCart();
  const navigate = useNavigate();
  const country = "NG";
  const currency = "₦";
  const savedItems: any[] = [];
  const handleAddItemToCart = (item: any) => console.log("Added to cart", item);
  const handleRemoveItemFromCart = (item: any) =>
    console.log("Removed from cart", item);

  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const exists = savedItems.some(
      (item) =>
        item.productCatalogueItemUniqueReferenceNumber ===
        productItem.productCatalogueItemUniqueReferenceNumber,
    );
    setIsSaved(exists);
  }, [savedItems, productItem.productCatalogueItemUniqueReferenceNumber]);

  const storeNameWithDash = textWithOrWithOutHypen({
    text: productItem?.merchantName,
    withHypen: true,
  });

  const productNameWithDash = textWithOrWithOutHypen({
    text: productItem?.name,
    withHypen: true,
  });

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart(productItem);
      return;
    }

    handleAddItemToCart({ ...productItem, isSavedItem: false });
  };

  const rewardText =
    productItem?.loyaltyPromotion?.rewardEffect ===
    RewardRuleEffects.AddDiscount
      ? `${parseFloat(productItem?.loyaltyPromotion?.rewardEffectValue || "0") * 10}% off this item` // Fixed math just for mock display
      : productItem?.loyaltyPromotion?.rewardEffect ===
          RewardRuleEffects.AddPoints
        ? `Get ${parseFloat(
            productItem?.loyaltyPromotion?.rewardEffectValue || "0",
          )} points on this item`
        : productItem?.loyaltyPromotion?.rewardEffect ===
            RewardRuleEffects.AddFreeItem
          ? `Get ${parseFloat(
              productItem?.loyaltyPromotion?.rewardEffectValue || "0",
            )} free item on this item`
          : "";

  const currentPrice =
    productItem?.loyaltyPromotion?.rewardEffect ===
    RewardRuleEffects.AddDiscount
      ? calculateDiscount(
          parseFloat(`${productItem?.price || 0}`),
          productItem?.loyaltyPromotion?.rewardEffectValue || 0,
          "priceAfterDiscount",
        )
      : productItem?.price;

  const isItemInCart = cartItems
    .map(
      ({ productCatalogueItemUniqueReferenceNumber }) =>
        productCatalogueItemUniqueReferenceNumber,
    )
    .includes(productItem.productCatalogueItemUniqueReferenceNumber);

  return (
    <section className="border border-py_black-100 rounded-2xl border-solid p-2 transition ease-in-out duration-300 hover:shadow-[0px_8px_24px_8px_rgba(18,18,18,0.08)] bg-white">
      <Link
        to={"#"}
        className={`
          ${"h-[195px] md:h-[320px] xl:h-80"}
          flex relative rounded-lg w-full overflow-hidden bg-gray-50`}>
        {productItem?.productImages &&
        productItem?.productImages.length >= 1 ? (
          <img
            src={productItem?.productImages[0].documentAbsoluteUri}
            alt={productItem?.name}
            className="w-full h-full object-cover cursor-pointer"
          />
        ) : (
          <div className="w-full h-full rounded-lg bg-gray-200 text-gray-500 flex flex-col items-center justify-center cursor-pointer">
            {productItem?.name || "No Image"}
          </div>
        )}
      </Link>

      <h5 className="truncate text-py_black-950 font-medium text-[0.875rem] leading-5 my-2">
        <Link to={"#"} className="text-gray-900 hover:text-black">
          {productItem?.name}
        </Link>
      </h5>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        {/* Price */}
        <div className="font-semibold text-[#121212] flex items-center text-sm leading-[120%] tracking-[-0.64px] md:tracking-[-0.96px]">
          {productItem?.storeRegionCurrencySymbol || currency}
          {currentPrice?.toLocaleString()}
        </div>
        {/* Old price */}
        {productItem?.loyaltyPromotion?.rewardEffect ===
          RewardRuleEffects.AddDiscount && (
          <del className="text-gray-400 font-light text-[0.65rem] leading-[120%] tracking-[-0.4px] md:text-xs md:tracking-[-0.56px]">
            {currency}
            {(productItem?.price || 0).toLocaleString()}
          </del>
        )}
        {/* Discount */}
        {rewardText && (
          <RewardTypeIcon
            text={rewardText}
            rewardType={productItem?.loyaltyPromotion?.rewardEffect}
          />
        )}
      </div>

      <button
        onClick={() => {
          if (!isItemInCart) {
            handleAddToCart();
          } else {
            navigate("/paylink/session");
          }
        }}
        disabled={addingToCart}
        className="w-full flex gap-2 justify-center items-center rounded-lg text-gray-900 font-medium text-[0.8rem] py-[0.65rem] h-[2.5rem] border border-gray-900 bg-white hover:bg-gray-50 transition-colors mt-3">
        {isItemInCart ? (
          "View cart"
        ) : (
          <>
            <AddIcon /> {addingToCart ? "Adding..." : "Add to cart"}
          </>
        )}
      </button>
    </section>
  );
};
